import { Col, Form, FormFeedback, FormGroup, Input, Label, Row } from "reactstrap";
import { IIndividual, IIndividualInput } from "../../../../types/individual";
import { SaveIcon } from "../../../common/icons";
import useIndividualScope from "../../../../hook/useIndividualScope";
import { GET_INDIVIDUAL, GET_INDIVIDUAL_DETAILS, UPDATE_INDIVIDUAL } from "../../../../graphql/individual-gql";
import { useApolloClient, useMutation } from "@apollo/client";
import { ChangeEvent, useEffect, useState } from "react";
import { ORG_VALUE_LISTS } from "../../../../graphql/valuelist-gql";
import Select from "../../../common/select/select";
import { IValueListLight } from "../../../../types/valuelist";
import { notifyError, notifySuccess } from "../../../../services/notification";
import { GET_ORG_POSITIONS, GET_ORG_ROLES } from "../../../../graphql/position-gql";
import { IOrg } from "../../../../types/org";
import { IPosition } from "../../../../types/position";
import useAuth from "../../../../hook/useAuth";
import { fullNameAutoFormat } from "../../../../utils/format-fullname";
import Button from "../../../common/button";
import SpinnerOverlay from "../../../common/spinner/spinner";
import { normalizeApostrophes, normalizeWhiteSpaces } from "../../../../utils/commons";

type IndividualInformationProp = {
    individual:IIndividual;
    org:IOrg|undefined;
    position:IPosition|undefined;
    onNameUpdate:(fullName:string)=>void;
    orgReadOnly:boolean;
}

const IndividualInformation:React.FC<IndividualInformationProp> = ({individual,org,position,onNameUpdate,orgReadOnly}) => {

    const [individualInput,setIndividualInput] = useState<IIndividualInput>({
        firstName: individual.firstName || '',
        lastName: individual.lastName || '',
        middleName: individual.middleName || '',
        genderId: individual.gender?.id || 0,
        x509: individual.x509 || '',
        honorificId: individual.honorific?.id || 0
    });
    const [firstnameError,setFirstnameError] = useState<string>('')
    const [lastnameError,setLastnameError] = useState<string>('')
    const [middlenameError,setMiddlenameError] = useState<string>('')
    const client = useApolloClient()
    const { orgValueLists } =  client.readQuery({query: ORG_VALUE_LISTS})
    const {editable,inScope,readonly:indiReadOnly} = useIndividualScope(individual.individualId);
    const {isAdmin, isCoordinator, isFrenchTranslator} = useAuth();

    const [updateIndividual, {loading}] = useMutation(UPDATE_INDIVIDUAL);

    const onFirstNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        if(!event.target.value || event.target.value.match(/^\s*$/)) {
            setFirstnameError('First name is required.')
        } else if(event.target.value.length > 100){
            setFirstnameError('First name cannot exceed 100 characters.')
        }else {
            setFirstnameError('')
        }
        setIndividualInput({...individualInput,firstName:event.target.value})
    }

    const onLastnameChange = (event: ChangeEvent<HTMLInputElement>) => {
        if(!event.target.value || event.target.value.match(/^\s*$/)){
            setLastnameError('Last name is required.')
        }else if(event.target.value.length > 100){
            setLastnameError('Last name cannot exceed 100 characters.')
        }else{
            setLastnameError('')
        }
        setIndividualInput({...individualInput,lastName:event.target.value})
    }

    const onMiddleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        if(event.target.value.length > 100){
            setMiddlenameError('middle name cannot exceed 100 characters.')
        }else{
            setMiddlenameError('')
        }
        setIndividualInput({...individualInput,middleName:event.target.value})
    }

    const onHonorificChange = (event: ChangeEvent<HTMLSelectElement>) => {
      setIndividualInput({ ...individualInput, honorificId: parseInt(event.target.value) })
    }

    const onGenderChange = (event: ChangeEvent<HTMLSelectElement>) => {
        setIndividualInput({ ...individualInput, genderId: parseInt(event.target.value) })
    }

    const onX509Change = (event: ChangeEvent<HTMLInputElement>)=> {
        setIndividualInput({ ...individualInput, x509: event.target.value })
    }


    useEffect(()=>{
        //console.log("individual:",individual)
        individual &&
            setIndividualInput(
                {
                    firstName: individual.firstName,
                    lastName: individual.lastName,
                    middleName: individual.middleName,
                    genderId: individual.gender?.id,
                    honorificId:individual.honorific?.id||0,
                    x509:individual.x509
                })

    },[individual])

    useEffect(()=>{
       
        onNameUpdate(fullNameAutoFormat(individualInput.firstName,individualInput.middleName,individualInput.lastName))

    },[individualInput.firstName,individualInput.middleName,individualInput.lastName])


    const onUpdateHandle = () => {
        let correctedFirstName= normalizeApostrophes(individualInput.firstName)
        let correctedLastName = normalizeApostrophes(individualInput.lastName)
        let correctedMiddleName = normalizeApostrophes(individualInput.middleName)
        individualInput.firstName= normalizeWhiteSpaces(correctedFirstName)
        individualInput.lastName= normalizeWhiteSpaces(correctedLastName)
        individualInput.middleName= normalizeWhiteSpaces(correctedMiddleName)

        //console.log("individualInput:",individualInput)
        updateIndividual({
            variables:{individualId:individual.individualId,input:individualInput},
            refetchQueries: getRefetchQueries(),
            awaitRefetchQueries: true,
            onCompleted: (data) => {
                data && notifySuccess()
            },
            onError: (error) => {
              if (error.graphQLErrors){
                error.graphQLErrors.forEach((graphQLError) => {
                    if(graphQLError.extensions){
                        if(graphQLError.extensions.field === "firstName"){
                            setFirstnameError(error.message)
                        }else if(graphQLError.extensions.field === "lastName"){
                            setLastnameError(error.message)
                        }else if(graphQLError.extensions.field === "middleName"){
                            setMiddlenameError(error.message)
                        }
                    }
                   
                    notifyError(error.message)
                })
                
                
              }
            },
        })
    }

    const getRefetchQueries = () => {
        let refetchQueries = [];
        if(org?.id)
            if(position?.isRole)
                refetchQueries.push({query: GET_ORG_ROLES,variables: { orgId: org.id }});
            else
                refetchQueries.push({query: GET_ORG_POSITIONS,variables: { orgId: org.id }});

        if(individual.positionAssignmentId)
            refetchQueries.push({query: GET_INDIVIDUAL_DETAILS,variables: { positionAssignmentId: individual.positionAssignmentId,individualId:individual.individualId }})
        else
            refetchQueries.push({query: GET_INDIVIDUAL,variables: { individualId:individual.individualId }})
        return refetchQueries;
    }

    const honorific = () =>{
        if(individualInput.honorificId && orgValueLists?.posHonorifics){
            const orgValueList = orgValueLists.posHonorifics.find((item:IValueListLight)=>item.id===individualInput.honorificId)
            return orgValueList?orgValueList.label:'';
        }
    }

    const gender = () =>{
        if(individualInput.genderId && orgValueLists?.genderTypes){
            const orgValueList = orgValueLists.genderTypes.find((item:IValueListLight)=>item.id===individualInput.genderId)
            return orgValueList?orgValueList.label:'';
        }
    }

    function isSaveDisabled(): boolean {
        if (indiReadOnly || orgReadOnly) return true;

        //verify that first / last names are not empty
        if (!individualInput.firstName || individualInput.firstName.trim() === '')
            return true;

        if (!individualInput.lastName || individualInput.lastName.trim() === '')
           return true;

        return false;
    }

    return(<>{loading && <SpinnerOverlay></SpinnerOverlay>}
        <Form id="IndividualInformationForm">
            <Row>
                <Col md={6}>
                    <FormGroup row>
                        <Label for="firstName" sm={5}>First Name (required):</Label>
                        {!isFrenchTranslator && <Col sm={6}>
                            <Input id="firstName" name="firstName" defaultValue={individualInput?.firstName?? ""} onChange={onFirstNameChange} invalid={!!firstnameError} required disabled={!editable||orgReadOnly} />
                            <FormFeedback>{firstnameError}</FormFeedback>
                        </Col>}
                        {isFrenchTranslator && <Col sm={6}>
                            <div className="top-2px">{individualInput?.firstName || ''}</div>  
                        </Col>
                        }
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup row>
                        <Label for="lastName" sm={5}>Last name (required):</Label>
                        {!isFrenchTranslator &&<Col sm={6}>
                            <Input id="lastName" name="lastName" defaultValue={individualInput?.lastName?? ""} onChange={onLastnameChange} required invalid={!!lastnameError} disabled={!editable||orgReadOnly}/>
                            <FormFeedback>{lastnameError}</FormFeedback>
                        </Col>
                        }
                        {isFrenchTranslator && <Col sm={6}>
                            <div className="top-2px">{individualInput?.lastName || ''}</div>
                        </Col>
                        }
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <FormGroup row>
                        <Label for="middleName" sm={5}>Middle Name/Initial:</Label>
                        {!isFrenchTranslator &&<Col sm={6}>
                            <Input id="middleName" name="middleName" defaultValue={individualInput?.middleName?? ""} invalid={!!middlenameError} onChange={onMiddleNameChange} disabled={!editable||orgReadOnly}/>
                            <FormFeedback>{middlenameError}</FormFeedback>
                        </Col>
                         }
                         {isFrenchTranslator && <Col sm={6}>
                         <div className="top-2px"> {individualInput?.middleName || ''}</div>
                         </Col>
                         }
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup row>
                        <Label for="honorific" sm={5}>Honorific:</Label>
                        {!isFrenchTranslator &&<Col sm={6}>
                            <Select id="honorific" name="honorific" defaultValue={individualInput?.honorificId ?? 0} onChange={onHonorificChange} disabled={indiReadOnly||orgReadOnly}>
                                <option key={0} value={0}>None </option>
                                {orgValueLists.honorificTitles.map((item: IValueListLight) => <option key={item.id} value={item.id}>{item.label}</option>)}
                            </Select>
                        </Col>
                         }
                         {isFrenchTranslator && <Col sm={6}>
                            <div className="top-2px">{honorific()}</div>
                         </Col>
                         }
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <FormGroup row>
                        <Label for="gender" sm={5}>Gender (For French):</Label>
                        {!isFrenchTranslator &&<Col sm={6}>
                            <Select id="gender" name="gender" defaultValue={individualInput?.genderId?? 0} onChange={onGenderChange} required disabled={indiReadOnly||orgReadOnly}>
                                {orgValueLists.genderTypes.map((item: IValueListLight) => <option key={item.id} value={item.id}>{item.label}</option>)}
                            </Select>
                        </Col>
                        }
                        {isFrenchTranslator && <Col sm={6}>
                          <div className="top-2px"> {gender()}</div>
                        </Col>
                        }
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup row>
                        <Label for="x509" sm={5}>x509:</Label>
                        {!isFrenchTranslator &&<Col sm={6}>
                            <Input id="x509" name="x509" defaultValue={individualInput?.x509 || ''} onChange={onX509Change} disabled={!isAdmin}/>
                        </Col>
                        }
                        {isFrenchTranslator && <Col sm={6}>
                           <div className="top-2px">{individualInput?.x509 || ''}</div>
                        </Col>
                        }
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    {!isFrenchTranslator &&<Button color="primary" disabled={isSaveDisabled()} onClick={onUpdateHandle}><SaveIcon/><span> Save Changes</span></Button>}
                </Col>
            </Row>
        </Form>
        </>
    )

}

export default IndividualInformation;
